import type {
  PatientGetAllResponse,
  PatientQuerySearch,
  PatientQuerySearchResponse,
  PatientResponse,
  PatientSearch,
  PersonContact,
  PersonGeneralGetResponse,
  UpdateContactPatient,
  UpdateGeneralPatient,
} from '@quromedical/models';
import { compact } from '@quromedical/utils';
import { trpcClient } from 'trpc';

export enum PatientEndpoint {
  Base = '',
  Coverage = 'coverage',
  Admit = 'admission',
}

export class PatientApi {
  private readonly patientRoot = 'api/aggregate/patient/';

  private createPath(patientId: string, endpoint: string, resourceId?: string) {
    const basePath = `${this.patientRoot}${patientId}`;

    return compact([basePath, endpoint, resourceId]).join('/');
  }

  /**
   * Get an SWR Key associated with the `getPatient` function.
   */
  public getPatientSWRKey = (
    patientId: string,
    ...endpoint: PatientEndpoint[]
  ): string => this.createPath(patientId, endpoint.join('_'));

  public async getPatient(patientId: string): Promise<PatientResponse> {
    const result = await trpcClient.patient.getById.query({ id: patientId });

    return result;
  }

  public async updateContactPatient(
    patientId: string,
    body: PersonContact,
  ): Promise<UpdateContactPatient> {
    return await trpcClient.patient.updatePatientContact.mutate({
      id: patientId,
      contact: body,
    });
  }

  public async updateGeneralPatient(
    params: UpdateGeneralPatient,
  ): Promise<PersonGeneralGetResponse> {
    return await trpcClient.patient.updatePatientGeneral.mutate(params);
  }

  public async getPatients(
    params: PatientSearch,
  ): Promise<PatientGetAllResponse> {
    return await trpcClient.patient.getAll.query(params);
  }

  public async search(
    request: PatientQuerySearch,
  ): Promise<PatientQuerySearchResponse> {
    return await trpcClient.patient.search.query(request);
  }
}
